import React from "react";
import { Button, Chip, Stack, Typography } from "@mui/material";
import EmergencyIcon from "@mui/icons-material/Emergency";

import { useEventCalendarValues } from "../../../routes/calendar/calendar-context";

export interface FilterPatientOption {
  patientId: string;
  label: string;
}

interface PatientFilterProps {
  patients: FilterPatientOption[];
}

const PatientFilter: React.FC<PatientFilterProps> = ({ patients }) => {
  const {
    visiblePatientIds,
    hasInteractedWithPatientFilter,
    resetVisiblePatientFilter,
    toggleVisiblePatientId,
    updateVisiblePatientIds,
  } = useEventCalendarValues();

  const isSelected = (patientId: string) => {
    return !hasInteractedWithPatientFilter || visiblePatientIds.includes(patientId);
  };

  const handlePatientClick = (patientId: string) => {
    if (!hasInteractedWithPatientFilter) {
      updateVisiblePatientIds(
        patients
          .map((patient) => patient.patientId)
          .filter((currentPatientId) => currentPatientId !== patientId),
      );

      return;
    }

    toggleVisiblePatientId(patientId);
  };

  return (
    <Stack
      sx={{
        px: 1.25,
        py: 1.25,
        borderRadius: 1.5,
        backgroundColor: "#F7F9FC",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Patients
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              onClick={resetVisiblePatientFilter}
              sx={{ minWidth: 0, px: 0.5, fontSize: "0.68rem", fontWeight: 700 }}
            >
              ALL
            </Button>
            <Button
              size="small"
              onClick={() => updateVisiblePatientIds([])}
              sx={{ minWidth: 0, px: 0.5, fontSize: "0.68rem", fontWeight: 700 }}
            >
              NONE
            </Button>
          </Stack>
        </Stack>
        {patients.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No patients in range
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {patients.map((patient) => {
              const selected = isSelected(patient.patientId);

              return (
                <Chip
                  key={patient.patientId}
                  icon={<EmergencyIcon fontSize="inherit" />}
                  label={patient.label}
                  size="small"
                  onClick={() => handlePatientClick(patient.patientId)}
                  sx={{
                    height: 24,
                    borderRadius: 999,
                    cursor: "pointer",
                    backgroundColor: "#DBEAFE",
                    color: "#0077E6",
                    border: selected ? "1px solid transparent" : "1px solid rgba(0, 119, 230, 0.24)",
                    opacity: selected ? 1 : 0.42,
                    boxShadow: selected ? "0px 2px 8px rgba(0, 119, 230, 0.18)" : "none",
                    transition: "transform 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease",
                    userSelect: "none",
                    "&:hover": {
                      opacity: selected ? 0.92 : 0.62,
                      transform: "translateY(-1px)",
                    },
                    ".MuiChip-label": {
                      px: 1,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      lineHeight: 1,
                    },
                    ".MuiChip-icon": {
                      color: "#0077E6",
                      fontSize: "0.78rem",
                      ml: 0.6,
                    },
                  }}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default PatientFilter;